/**
 * Settings Store - User preferences and configuration
 * Matches architecture specification in docs/architecture/10-frontend-architecture.md
 */

import { writable } from 'svelte/store';
import { loadSettings, saveSettings } from '../utils/storage';
import type { Settings } from '../types';

/**
 * Create the settings store with localStorage persistence
 */
function createSettingsStore() {
  const initialSettings: Settings = loadSettings() || {
    volume: 0.8,
    soundEnabled: true,
    controlScheme: 'keyboard'
  };

  const { subscribe, set, update } = writable<Settings>(initialSettings);

  return {
    subscribe,

    /**
     * Set the volume level (0-1)
     */
    setVolume: (volume: number) => {
      // Clamp volume between 0 and 1
      const clampedVolume = Math.max(0, Math.min(1, volume));

      update(settings => {
        const newSettings = { ...settings, volume: clampedVolume };
        saveSettings(newSettings);
        return newSettings;
      });
    },

    /**
     * Toggle sound on/off
     */
    toggleSound: () => {
      update(settings => {
        const newSettings = { ...settings, soundEnabled: !settings.soundEnabled };
        saveSettings(newSettings);
        return newSettings;
      });
    },

    /**
     * Enable sound
     */
    enableSound: () => {
      update(settings => {
        const newSettings = { ...settings, soundEnabled: true };
        saveSettings(newSettings);
        return newSettings;
      });
    },

    /**
     * Disable sound
     */
    disableSound: () => {
      update(settings => {
        const newSettings = { ...settings, soundEnabled: false };
        saveSettings(newSettings);
        return newSettings;
      });
    },

    /**
     * Set the control scheme
     */
    setControlScheme: (scheme: 'drag' | 'tap' | 'keyboard') => {
      update(settings => {
        const newSettings = { ...settings, controlScheme: scheme };
        saveSettings(newSettings);
        return newSettings;
      });
    },

    /**
     * Reset settings to defaults
     */
    reset: () => {
      const defaultSettings: Settings = {
        volume: 0.8,
        soundEnabled: true,
        controlScheme: 'keyboard'
      };
      set(defaultSettings);
      saveSettings(defaultSettings);
    }
  };
}

export const settings = createSettingsStore();
